// Test mode (docs/areas/learn.md): the same questions as a deck, on a loop, on a scope he chose. Pure
// pieces the views call: the pool a scope gives, a no-repeat shuffle that spreads patterns, and the
// summary. Library code: imports a kind's schema or apply, never its index.
import type { ConceptResource } from '@/kinds/learn/apply'
import type { Result } from '@/kinds/learn/schema'
import type { Deck, ExerciseCard, Records } from './learnDeck'

export const TEST_LENGTHS = [10, 20, 40] as const
/** 0 is endless: it keeps going until he stops. */
export type TestLength = 0 | (typeof TEST_LENGTHS)[number]

export type TestScope = 'all' | 'creational' | 'structural' | 'behavioral' | 'pick' | 'weakest'
export const TEST_SCOPES: TestScope[] = ['all', 'creational', 'structural', 'behavioral', 'pick', 'weakest']
export const SCOPE_WORD: Record<TestScope, string> = {
  all: 'All patterns',
  creational: 'Creational',
  structural: 'Structural',
  behavioral: 'Behavioral',
  pick: 'Pick patterns',
  weakest: 'What I’m weakest at',
}

export interface TestSetup {
  scope: TestScope
  /** concept ids, for scope `pick` */
  patterns: string[]
  length: TestLength
  timer: boolean
}

export const DEFAULT_TEST: TestSetup = { scope: 'all', patterns: [], length: 10, timer: true }

/** The last setup, as Settings kept it; anything unknown falls back to the default. */
export const setupFrom = (saved?: {
  scope?: string
  patterns?: string[]
  length?: number
  timer?: boolean
}): TestSetup => ({
  scope: TEST_SCOPES.find((s) => s === saved?.scope) ?? DEFAULT_TEST.scope,
  patterns: saved?.patterns ?? [],
  length: saved?.length === 0 ? 0 : (TEST_LENGTHS.find((n) => n === saved?.length) ?? DEFAULT_TEST.length),
  timer: saved?.timer ?? DEFAULT_TEST.timer,
})

const live = <R extends { deleted_at: string | null }>(rows: R[]) => rows.filter((r) => !r.deleted_at)

/** Every question (never a Learn card) with the concepts it is about. */
export const questionCards = (r: Pick<Records, 'concepts' | 'exercises' | 'edges'>): ExerciseCard[] => {
  const concepts = new Map(live(r.concepts).map((c) => [c.id, c]))
  const of = new Map<string, ConceptResource[]>()
  for (const e of r.edges) {
    if (e.deleted_at || e.relation !== 'exercises') continue
    const c = concepts.get(e.to_id)
    if (c) of.set(e.from_id, [...(of.get(e.from_id) ?? []), c])
  }
  return live(r.exercises).flatMap((x) =>
    x.spec.format !== 'learn' && of.get(x.id)?.length
      ? [{ format: x.spec.format, id: x.id, exercise: x, concepts: of.get(x.id)! } as ExerciseCard]
      : [],
  )
}

/** Concepts that are learning or new, or have a question last answered partly or wrong. */
export const weakConcepts = (r: Pick<Records, 'concepts' | 'exercises' | 'edges'>): Set<string> => {
  const weak = new Set(
    live(r.concepts)
      .filter((c) => c.status.familiarity !== 'known')
      .map((c) => c.id),
  )
  for (const card of questionCards(r))
    if (card.exercise.status.result === 'wrong' || card.exercise.status.result === 'partial')
      for (const c of card.concepts) weak.add(c.id)
  return weak
}

/** The concept ids a scope covers. */
export const scopeConcepts = (
  r: Pick<Records, 'concepts' | 'exercises' | 'edges'>,
  setup: Pick<TestSetup, 'scope' | 'patterns'>,
): Set<string> => {
  const all = live(r.concepts)
  switch (setup.scope) {
    case 'pick':
      return new Set(all.filter((c) => setup.patterns.includes(c.id)).map((c) => c.id))
    case 'weakest':
      return weakConcepts(r)
    case 'all':
      return new Set(all.map((c) => c.id))
    default:
      return new Set(all.filter((c) => c.spec.category?.toLowerCase() === setup.scope).map((c) => c.id))
  }
}

/** The questions a scope covers: a combination only when the scope covers all of its patterns. */
export const testPool = (
  r: Pick<Records, 'concepts' | 'exercises' | 'edges'>,
  setup: Pick<TestSetup, 'scope' | 'patterns'>,
): ExerciseCard[] => {
  const inScope = scopeConcepts(r, setup)
  return questionCards(r).filter((q) => q.concepts.every((c) => inScope.has(c.id)))
}

export const shuffle = <T>(xs: readonly T[], rng: () => number = Math.random): T[] => {
  const a = [...xs]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

const shares = (a: ExerciseCard | undefined, b: ExerciseCard) =>
  !!a && a.concepts.some((c) => b.concepts.some((d) => d.id === c.id))

/**
 * A draw from a pool: shuffled, no question twice until the whole pool has been used (then a fresh
 * shuffle), and no pattern twice in a row where the pool allows it. `next(n)` continues where the last
 * call stopped, so Endless is the same thing asked again.
 */
export const makeDraw = (pool: readonly ExerciseCard[], rng: () => number = Math.random) => {
  let bag = shuffle(pool, rng)
  let prev: ExerciseCard | undefined
  const take = (): ExerciseCard | undefined => {
    if (!pool.length) return undefined
    if (!bag.length) bag = shuffle(pool, rng)
    const at = bag.findIndex((q) => !shares(prev, q))
    return bag.splice(at < 0 ? 0 : at, 1)[0]
  }
  return {
    /** the next n questions */
    next(n: number): ExerciseCard[] {
      const got: ExerciseCard[] = []
      while (got.length < n) {
        const q = take()
        if (!q) break
        got.push(q)
        prev = q
      }
      return got
    },
  }
}

/** A test's first questions: its length, or a first batch when endless. */
export const BATCH = 10

export interface Answered {
  card: ExerciseCard
  /** unset when the timer ran out: the question was not got to */
  result?: Result
}

export interface PatternRow {
  id: string
  name: string
  url?: string
  /** how each question on it went, in order */
  results: (Result | 'skipped')[]
  /** the explanation of the first question missed, for a row to revisit */
  why?: string
}

export interface TestSummary {
  total: number
  right: number
  partly: number
  /** wrong, and the ones the timer ran out on */
  revisit: number
  strong: PatternRow[]
  toRevisit: PatternRow[]
  /** the questions missed (partly, wrong or timed out), for "just what I missed" */
  missed: ExerciseCard[]
}

export const summarise = (answered: Answered[]): TestSummary => {
  const rows = new Map<string, PatternRow>()
  const missed: ExerciseCard[] = []
  let right = 0
  let partly = 0
  let revisit = 0
  for (const a of answered) {
    const r = a.result
    if (r === 'right') right++
    else if (r === 'partial') partly++
    else revisit++
    if (r !== 'right') missed.push(a.card)
    for (const c of a.card.concepts) {
      const row = rows.get(c.id) ?? { id: c.id, name: c.spec.name, url: c.spec.source_url, results: [] }
      row.results.push(r ?? 'skipped')
      if (r !== 'right' && !row.why) row.why = a.card.exercise.spec.explanation
      rows.set(c.id, row)
    }
  }
  const missCount = (p: PatternRow) => p.results.filter((x) => x !== 'right').length
  const all = [...rows.values()]
  return {
    total: answered.length,
    right,
    partly,
    revisit,
    strong: all.filter((p) => !missCount(p)).sort((a, b) => a.name.localeCompare(b.name)),
    toRevisit: all
      .filter(missCount)
      .sort((a, b) => missCount(b) - missCount(a) || a.name.localeCompare(b.name)),
    missed,
  }
}

/** The plain score line: "14 of 20 right". */
export const scoreLine = (s: Pick<TestSummary, 'right' | 'total'>) => `${s.right} of ${s.total} right`

/**
 * The questions of a second go at what was missed: the missed ones first, then fresh ones on the same
 * patterns (those not already in), up to `length` (all of the missed even when that is more).
 */
export const missedRound = (
  missed: ExerciseCard[],
  pool: readonly ExerciseCard[],
  length: number,
  rng: () => number = Math.random,
): ExerciseCard[] => {
  const ids = new Set(missed.map((q) => q.id))
  const concerned = new Set(missed.flatMap((q) => q.concepts.map((c) => c.id)))
  const fresh = pool.filter((q) => !ids.has(q.id) && q.concepts.some((c) => concerned.has(c.id)))
  const lead = makeDraw(missed, rng).next(missed.length)
  const rest = makeDraw(fresh.length ? fresh : pool.filter((q) => !ids.has(q.id)), rng).next(
    Math.max(0, (length || BATCH) - lead.length),
  )
  return [...lead, ...rest]
}

type Read = Pick<Records, 'concepts' | 'exercises' | 'edges'>

/**
 * A test as a deck of questions only, or null when the scope has none. `missed` makes it a second go at
 * those questions. Endless starts with a batch and the deck asks `test.draw` for more as it goes.
 */
export const buildTest = (
  r: Read,
  setup: TestSetup,
  missed?: ExerciseCard[],
  rng: () => number = Math.random,
): Deck | null => {
  const pool = testPool(r, setup)
  if (!pool.length) return null
  const draw = makeDraw(pool, rng)
  const cards = missed?.length
    ? missedRound(missed, pool, setup.length, rng)
    : draw.next(setup.length || BATCH)
  return { mode: 'test', cards, test: { setup, pool, draw } }
}
